import React, { useState, useCallback, useEffect } from "react";
import {
  Upload,
  Music,
  Copy,
  CheckCircle,
  X,
  Calendar,
  Search,
  ExternalLink,
  Trash2,
  Filter,
  FileAudio
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import {
  getAllWatermarkSongs,
  uploadAudioFilesWithWaterMark,
} from "../redux/reduxSlices/audioUploadSlice";
import { getUserProfile } from "../redux/reduxSlices/userProfileSlice";
import DeleteConfirmationPopup from "./songUpload/DeleteConfirmationPopup";
import { getOrderById } from "../redux/reduxSlices/ordersSlice";

const AudioUploadPage = ({ order_Id }) => {
  const [orderId, setOrderId] = useState(order_Id ? order_Id : "");
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [copiedOrderId, setCopiedOrderId] = useState(null);
  const [openDeleteSongPopup, setOpenDeleteSongPopup] = useState(false);
  const [deleteOrder, setDeleteOrder] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [searchOrderId, setSearchOrderId] = useState("");
  const [searchDate, setSearchDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const {
    loading,
    success,
    fetchError,
    orderByIdData,
  } = useSelector((state) => state.orders);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isLoading, uploadedAudio, getAllSongsData } = useSelector(
    (state) => state.audioUpload
  );

  const allSongs = getAllSongsData || [];

  useEffect(() => {
    dispatch(getAllWatermarkSongs());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getOrderById(order_Id))
  }, [order_Id])
  useEffect(() => {
    setCompanyName(orderByIdData["Company Name:"] ? orderByIdData["Company Name:"] : orderByIdData['company_name'] ? orderByIdData['company_name'] : "")
  }, [orderByIdData])

  const getValidAudioFiles = (files) => {
    const all = Array.from(files || []).filter(
      (f) => f.type && f.type.startsWith("audio/")
    );
    if (all.length > 7) {
      toast.error("Maximum 7 audio files allowed");
      return all.slice(0, 7);
    }
    if (all.length === 0) {
      toast.error("Please select audio files (mp3, wav, m4a, aac)");
    }
    return all;
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const valid = getValidAudioFiles(e.dataTransfer.files);
    if (valid && valid.length > 0) {
      setSelectedFiles(valid);
    }
  };

  const onFileChange = (e) => {
    const valid = getValidAudioFiles(e.target.files);
    if (valid && valid.length > 0) {
      setSelectedFiles(valid);
    }
    e.target.value = "";
  };

  const handleUpload = useCallback(async () => {
    if (!orderId?.trim()) {
      toast.error("Please enter Order ID");
      return;
    }
    if (!selectedFiles || selectedFiles.length === 0) {
      toast.error("Please select audio files");
      return;
    }

    setUploading(true);
    try {
      const resultAction = await dispatch(
        uploadAudioFilesWithWaterMark({
          order_id: orderId.trim(),
          files: selectedFiles,
          company_name: companyName.trim()
        })
      );

      if (uploadAudioFilesWithWaterMark.fulfilled.match(resultAction)) {
        toast.success("Audio uploaded!");
        // setCompanyName('');
        setSelectedFiles([]);
        dispatch(getAllWatermarkSongs());
      } else {
        toast.error("Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Upload error");
    } finally {
      setUploading(false);
    }
  }, [dispatch, orderId, selectedFiles, companyName]);

  const handleCopyFullRoute = async (oid) => {
    const fullUrl = `${window.location.origin}/watermarked-jingles/${oid}`;
    try {
      await navigator.clipboard.writeText(fullUrl);
      setCopiedOrderId(oid);
      toast.success("Link copied!");
      setTimeout(() => setCopiedOrderId(null), 1500);
    } catch (err) {
      console.error("Copy failed", err);
      toast.error("Failed to copy");
    }
  };

  const handleOpenOrder = (oid) => {
    navigate(`/watermarked-jingles/${oid}`);
  };

  const normalizeOrderId = (id = "") =>
    String(id).replace(/^ORD-/, "").toLowerCase();

  const filteredSongs = allSongs.filter((song) => {
    const matchesOrderId = searchOrderId
      ? normalizeOrderId(song.order_id) === normalizeOrderId(searchOrderId)
      : true;

    const matchesDate = searchDate
      ? new Date(song.created_at || song.uploaded_at)
        .toDateString()
        .includes(new Date(searchDate).toDateString())
      : true;

    return matchesOrderId && matchesDate;
  });

  const handleDeleteClick = (order_id, song_id) => {
    setDeleteOrder({
      order_id: order_id,
      song_id: song_id,
      collection_type: "with_watermark"
    });
    setOpenDeleteSongPopup(true);
  };

  return (
    <div className="min-h-[80vh] bg-gray-50 p-2">
      <div className="w-full">
        {/* Header - Compact */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between px-3 py-2 border-b border-gray-200 bg-white mb-3">
          <div className="flex items-center gap-2 mb-2 sm:mb-0">
            <div className="p-1 bg-orange-100 rounded">
              <Music className="w-4 h-4 text-orange-600" />
            </div>
            <div>
              <h1 className="text-base font-bold text-orange-600">
                Upload Watermarked Jingles
              </h1>
              <p className="text-xs text-gray-600">
                Upload audio files and manage URLs
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* LEFT SIDE - Upload Section */}
          <div className="space-y-3">
            <div className="bg-white rounded-lg border border-gray-200 p-3 max-md:max-w-[90vw]">
              <h2 className="text-sm font-semibold text-orange-600 mb-3">
                Upload Audio Files
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Order ID
                  </label>
                  <input
                    type="text"
                    value={orderId}
                    onChange={(e) => setOrderId(e.target.value)}
                    placeholder="Order ID"
                    className="w-full py-1.5 px-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="Company name"
                    className="w-full py-1.5 px-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                  />
                </div>
              </div>

              {/* File Upload */}
              <div
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                className={`relative border-2 border-dashed rounded p-2 transition-colors ${dragActive
                  ? "border-orange-500 bg-orange-50"
                  : "border-gray-200"
                  }`}
              >
                <div className="text-center">
                  <Upload className="w-4 h-4 mx-auto mb-2 text-gray-400" />
                  <p className="text-gray-600 text-xs mb-1">
                    Drag & drop audio files
                  </p>
                  <p className="text-xs text-gray-500 mb-2">or</p>
                  <input
                    type="file"
                    multiple
                    accept=".mp3,.wav,.m4a,.aac,audio/*"
                    onChange={onFileChange}
                    className="hidden"
                    id="audio-file-input"
                  />
                  <label
                    htmlFor="audio-file-input"
                    className="cursor-pointer inline-flex items-center gap-1 px-3 py-1.5 text-white rounded text-sm font-medium bg-gradient-to-br from-orange-500 to-orange-600"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    Choose Files
                  </label>
                </div>
              </div>

              {selectedFiles.length > 0 && (
                <div className="mt-2 bg-orange-50 border border-orange-200 rounded p-2">
                  <p className="text-xs font-medium text-gray-700">
                    {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
                  </p>
                  <ul className="text-xs text-gray-600 mt-1 space-y-0.5">
                    {selectedFiles.map((file, idx) => (
                      <li key={idx} className="truncate">{file.name}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex flex-wrap gap-2 mt-3">
                <button
                  onClick={handleUpload}
                  disabled={selectedFiles.length === 0 || uploading || !orderId.trim()}
                  className="px-3 py-1.5 text-white text-sm rounded font-medium bg-gradient-to-br from-orange-500 to-orange-600 disabled:opacity-50 flex items-center gap-1"
                >
                  {uploading ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-3.5 h-3.5" />
                      Upload
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setOrderId("");
                    setSelectedFiles([]);
                    setCompanyName('');
                  }}
                  className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded font-medium hover:bg-gray-300 flex items-center gap-1"
                >
                  <X className="w-3.5 h-3.5" />
                  Reset
                </button>
              </div>

              {uploadedAudio?.uploaded_files && uploadedAudio.uploaded_files.length > 0 && (
                <div className="mt-4 p-2 bg-green-50 border border-green-200 rounded">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <h3 className="text-sm font-medium text-gray-800">Upload Successful</h3>
                  </div>
                  <div className="space-y-1.5">
                    {uploadedAudio.uploaded_files.map((url, index) => (
                      <div key={index} className="bg-white rounded p-1.5 border border-green-100">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5">
                            <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs">{index + 1}</span>
                            </div>
                            <p className="text-xs text-gray-700 truncate max-w-[180px]">
                              File {index + 1}
                            </p>
                          </div>
                          <button
                            onClick={() => navigator.clipboard.writeText(url.file_url)}
                            className="text-xs px-2 py-1 bg-green-500 text-white rounded"
                          >
                            Copy URL
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT SIDE - Manage URLs */}
          <div className="space-y-3">
            <div className="bg-white rounded-lg border border-gray-200 p-3 max-md:max-w-[90vw]">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                <h2 className="text-sm font-semibold text-orange-600">
                  Manage URLs
                </h2>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded flex items-center gap-1"
                  >
                    <Filter className="w-3 h-3" />
                    Filters
                  </button>
                </div>
              </div>

              {/* Filters - Collapsible */}
              {showFilters && (
                <div className="mb-3 p-2 bg-gray-50 rounded border border-gray-200">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      type="text"
                      placeholder="Search Order ID"
                      value={searchOrderId}
                      onChange={(e) => setSearchOrderId(e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                    />
                    <input
                      type="date"
                      value={searchDate}
                      onChange={(e) => setSearchDate(e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                    />
                    <button
                      onClick={() => {
                        setSearchOrderId("");
                        setSearchDate("");
                      }}
                      className="col-span-2 px-2 py-1 text-white text-sm rounded bg-gradient-to-br from-orange-500 to-orange-600"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              )}

              {/* SONG LIST */}
              <div className="max-h-[60vh] overflow-y-auto">
                {isLoading ? (
                  <div className="text-center py-4">
                    <div className="inline-block w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-xs text-gray-500 mt-1">Loading...</p>
                  </div>
                ) : filteredSongs.length === 0 ? (
                  <div className="text-center py-8">
                    <FileAudio className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No songs found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* {console.log("ffffff",filteredSongs)} */}
                    {filteredSongs.map((order) => (
                      <div
                        key={order.order_id}
                        className="border border-gray-200 rounded p-3 bg-white"
                      >
                        {/* Order Header */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-2 px-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-800">
                              Order {order.order_id}
                            </span>
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleCopyFullRoute(order.order_id)}
                                className="text-xs px-2 py-1 rounded text-white bg-gradient-to-br from-orange-500 to-orange-600 flex items-center gap-1"
                                title="Copy link"
                              >
                                {copiedOrderId === order.order_id ? (
                                  <CheckCircle className="w-3 h-3" />
                                ) : (
                                  <Copy className="w-3 h-3" />
                                )}
                              </button>
                              <button
                                onClick={() => handleOpenOrder(order.order_id)}
                                className="text-xs px-2 py-1 rounded text-white bg-green-500 flex items-center gap-1"
                                title="Open"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </button>
                            </div>
                          </div>

                          <div className="">
                            <span className="text-xs text-gray-600 block">
                              {order.created_at
                                ? new Date(order.created_at).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                })
                                : "-"}
                            </span>
                            <span className="text-xs text-gray-700">
                              {order.songs?.length || 0} file(s)
                            </span>
                          </div>
                        </div>

                        {/* Songs List */}
                        {order.songs && order.songs.length > 0 ? (
                          <div className="space-y-1.5">
                            {order.songs.map((song, index) => (
                              <div
                                key={`${order.order_id}-${index}`}
                                className="bg-gray-50 rounded p-2 flex items-center justify-between gap-2"
                              >
                                <a
                                  href={song.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:underline truncate flex-1"
                                  title={song.url}
                                >
                                  {song.url.split('/').pop() || song.url}
                                </a>
                                <div className="flex gap-1">
                                  <button
                                    onClick={() =>
                                      navigator.clipboard.writeText(song.url).then(() =>
                                        toast.success("URL copied!")
                                      )
                                    }
                                    className="text-xs px-2 py-1 rounded text-white bg-gradient-to-br from-orange-500 to-orange-600"
                                    title="Copy URL"
                                  >
                                    Copy
                                  </button>
                                  <button
                                    onClick={() => handleDeleteClick(order.order_id, song.song_id)}
                                    className="text-xs px-2 py-1 rounded bg-red-500 text-white hover:bg-red-600"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-xs italic">No songs available</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmationPopup
        isOpen={openDeleteSongPopup}
        onClose={() => setOpenDeleteSongPopup(false)}
        deleteOrder={deleteOrder}
      />
    </div>
  );
};

export default AudioUploadPage;